/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import IntlTelInput, { intlTelInput } from "@intl-tel-input/react/with-utils";
import ErrorIcon from "@material-ui/icons/Error";
import { useI18n } from "hooks/i18n/i18n";
import { useRef } from "react";

import "intl-tel-input/styles";

import type { IntlTelInputRef } from "@intl-tel-input/react/with-utils";
import type { FC } from "react";

/**
 * 表示中の言語を Intl のロケールに直す。
 *
 * portal は zh_cn のようにアンダースコア区切りを使うが、Intl は BCP 47 しか
 * 受け付けない。解釈できない値を渡すと intl-tel-input は国名を空文字にするため、
 * その場合は英語の国名にフォールバックする。
 */
const toCountryNameLocale = (language: string): string => {
  try {
    return Intl.getCanonicalLocales(language.replace("_", "-"))[0] ?? "en";
  } catch (e) {
    console.error(e);
    return "en";
  }
};

/** 国の選択が無いときの既定。国内の利用が大半のため */
const DEFAULT_COUNTRY = "jp";

const INPUT_ID = "phone-number-field-input";

type Props = {
  /** 入力欄の見出し。新規登録と変更で変わるため呼び出し側が決める */
  label: string;
  /** 入力が妥当なときだけ E.164 の番号。妥当でなければ空文字 */
  onChangeNumber: (number: string) => void;
  onChangeValidity: (isValid: boolean) => void;
  /** 入力欄からフォーカスが外れたとき。エラーはこの後にだけ出す */
  onBlur: () => void;
  /** 入力途中に赤字を出さないため、表示の可否は呼び出し側が決める */
  showError: boolean;
  disabled: boolean;
};

/**
 * 国を選べる電話番号の入力欄。
 *
 * SMS で認証コードを送るため、携帯電話番号だけを妥当とする。固定電話・IP電話は
 * allowedNumberTypes で弾く。ただし北米のように番号計画上そもそも携帯と固定を
 * 区別しない国の固定電話は弾けない。
 */
export const PhoneNumberField: FC<Props> = ({
  label,
  onChangeNumber,
  onChangeValidity,
  onBlur,
  showError,
  disabled,
}) => {
  const { t, i18n } = useI18n();
  const itiRef = useRef<IntlTelInputRef>(null);
  const classes = styles();

  return (
    <div css={classes.container}>
      <label css={classes.label} htmlFor={INPUT_ID}>
        {label}
      </label>
      <div css={[classes.fields, showError && classes.fieldsError]}>
        <IntlTelInput
          ref={itiRef}
          initialCountry={DEFAULT_COUNTRY}
          countryOrder={[DEFAULT_COUNTRY]}
          countryNameLocale={toCountryNameLocale(i18n.language)}
          // 国番号を入力欄の外に出し、欄の中は国内番号だけにする
          separateDialCode
          allowedNumberTypes={["MOBILE"]}
          // 既定の判定は桁数が携帯として有り得るかしか見ず、050 のIP電話や
          // 090-0000-0000 のような欠番も通る。番号の一意性を複垢対策に使うため、
          // 番号計画と照合する厳密な判定にする
          usePreciseValidation
          // 入力した通りに見せる。整形させると国内プレフィックスの 0 を
          // 付けたときだけ整形が効かず、付けないときだけ効いて挙動が揃わない
          formatAsYouType={false}
          // 既定の例は区切り付きで国内プレフィックスも落ちた 90-1234-5678 になる。
          // 入力してほしい形と揃えて、数字だけの 09012345678 を見せる
          customPlaceholder={(placeholder, country) => {
            const digits = placeholder.replace(/\D/g, "");
            return country?.nationalPrefix == null
              ? digits
              : `${country.nationalPrefix}${digits}`;
          }}
          disabled={disabled}
          onChangeNumber={onChangeNumber}
          onChangeValidity={onChangeValidity}
          inputProps={{
            id: INPUT_ID,
            inputMode: "tel",
            autoComplete: "tel-national",
            onBlur,
          }}
        />
      </div>
      {showError ? (
        <p css={classes.error}>
          <ErrorIcon css={classes.errorIcon} />
          <span>{t(toErrorMessageKey(itiRef.current))}</span>
        </p>
      ) : (
        <p css={classes.hint}>{t("phoneNumber.inputHint")}</p>
      )}
    </div>
  );
};

/** 日本の国番号。これで始まる E.164 は日本の番号だけ */
const JAPAN_DIAL_CODE = "+81";

/**
 * E.164 の番号を表示用に整える。
 *
 * 国際表記は国内プレフィックスの 0 を落として +81 80-1111-1111 になるが、
 * 日本では 080-1111-1111 と書くのが普通なので、日本の番号だけは
 * 国番号と国内表記を並べて +81 080-1111-1111 と見せる。
 */
export const formatPhoneNumber = (number: string): string => {
  const { utils } = intlTelInput;
  if (utils === undefined) {
    // with-utils から読み込んでいるので undefined にはならない
    return number;
  }
  if (number.startsWith(JAPAN_DIAL_CODE)) {
    const national = utils.formatNumber(number, DEFAULT_COUNTRY, "NATIONAL");
    return `${JAPAN_DIAL_CODE} ${national}`;
  }
  // 国番号を含む番号なので iso2 は渡さなくてよい
  return utils.formatNumber(number, undefined, "INTERNATIONAL");
};

/**
 * いま表示すべき検証エラーの文言キーを返す。
 *
 * onChangeErrorCode は値が変わったときにしか呼ばれず、空欄の時点で既に
 * TOO_SHORT が入っているため、途中まで入力しても発火しない。
 * 表示する時点でインスタンスから読む。
 */
const toErrorMessageKey = (iti: IntlTelInputRef | null) => {
  const instance = iti?.getInstance();
  if (instance === null || instance === undefined) {
    return "phoneNumber.errorInvalidPhoneNumber" as const;
  }
  if (instance.getNumber() === "") {
    return "phoneNumber.errorRequired" as const;
  }
  const { VALIDATION_ERROR } = intlTelInput;
  switch (instance.getValidationError()) {
    case VALIDATION_ERROR.TOO_SHORT:
      return "phoneNumber.errorTooShort" as const;
    case VALIDATION_ERROR.TOO_LONG:
      return "phoneNumber.errorTooLong" as const;
    case VALIDATION_ERROR.INVALID_COUNTRY_CODE:
      return "phoneNumber.errorInvalidCountryCode" as const;
    default:
      return "phoneNumber.errorInvalidPhoneNumber" as const;
  }
};

const styles = () => {
  return {
    container: css({
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    }),
    label: css({
      margin: 0,
      fontSize: "15px",
      lineHeight: "20px",
      fontWeight: 500,
    }),
    /**
     * 国選択は既定では入力欄の内側に重ねて絶対配置される。国番号と番号は別の
     * 入力なので、それぞれ独立した枠として横に並べ直す。
     */
    fields: css({
      ".iti": {
        display: "flex",
        gap: "8px",
        width: "100%",
      },
      ".iti__country-container": {
        // 既定の absolute を外して並びに戻す。relative はドロップダウンの位置の基準
        position: "relative",
        flex: "none",
        width: "150px",
        height: "40px",
        padding: 0,
        border: "1px solid #d2d2d2",
        backgroundColor: "#fff",
      },
      ".iti__selected-country": {
        width: "100%",
        height: "100%",
        paddingLeft: "12px",
      },
      // 旗と矢印を包む箱を畳んで、旗・国名・国番号・矢印を1列に並べる
      ".iti__selected-country-primary": {
        display: "contents",
      },
      ".iti__selected-dial-code": {
        order: 1,
        marginLeft: "6px",
        fontSize: "16px",
        lineHeight: "24px",
      },
      // 三角形を border で描く既定を捨てて、デザイン通りの箱と矢印にする
      ".iti__arrow": {
        order: 2,
        position: "relative",
        width: "30px",
        height: "100%",
        margin: "0 0 0 auto",
        border: "none",
        transform: "none",
        backgroundColor: "#e0e0e0",
        // 矢印はライブラリが作る要素なのでコンポーネントを置けない。
        // 既定は border で描いた斜め線なので、塗りつぶしの三角に描き直す
        "&::after": {
          content: '""',
          position: "absolute",
          top: "50%",
          left: "50%",
          width: 0,
          height: 0,
          margin: "-3px 0 0 -5px",
          borderRight: "5px solid transparent",
          borderLeft: "5px solid transparent",
          borderTop: "6px solid #6f6f6f",
        },
      },
      ".iti__arrow--up": {
        margin: "0 0 0 auto",
        "&::after": {
          transform: "rotate(180deg)",
        },
      },
      ".iti input.iti__tel-input": {
        flex: 1,
        // 既定の min-width:100% は flex の中では入力欄が枠からはみ出す
        minWidth: 0,
        height: "40px",
        padding: "0 12px",
        // 国選択を入力欄に重ねる作りのため、ライブラリがその幅ぶんの
        // padding-left を style 属性で直に入れてくる。重ねるのをやめた今は
        // 文字が中途半端な位置から始まるだけなので打ち消す
        paddingLeft: "12px !important",
        fontSize: "16px",
        lineHeight: "24px",
        border: "1px solid #d2d2d2",
        borderRadius: 0,
        "&::placeholder": {
          color: "#9e9e9e",
        },
      },
    }),
    fieldsError: css({
      ".iti input.iti__tel-input": {
        border: "2px solid #e53935",
      },
    }),
    hint: css({
      margin: 0,
      color: "#6f6f6f",
      fontSize: "12px",
      lineHeight: "18px",
    }),
    error: css({
      display: "flex",
      margin: 0,
      gap: "6px",
      alignItems: "flex-start",
      color: "#e53935",
      fontSize: "13px",
      fontWeight: "bold",
      lineHeight: "20px",
    }),
    errorIcon: css({
      flex: "none",
      // アイコンは文字より大きく見えるので、1行の高さに収まる大きさにする
      fontSize: "16px",
      // 1行目の文字の中心に合わせる
      marginTop: "2px",
    }),
  };
};
